// src/pages/SubscriptionSuccess.js
import { useEffect, useRef, useState, useCallback } from "react";
import { trackEvent } from "../firebase/analytics";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "../hooks/useSubscription";
import { useAuth } from "../Context/AuthContext";
import { db } from "../firebase/config";
import { doc, updateDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { sendEmployerWelcomeEmail } from "../firebase/emailService";

export default function SubscriptionSuccess() {
  const { subscription, loading } = useSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();

  // was useState<"waiting" | "linking" | "done" | "error"> in TS — not needed in .js
  const [phase, setPhase] = useState("waiting");
  const wroteOnceRef = useRef(false);

  // mark post-checkout so AdminDashboard can skip an immediate redirect
  useEffect(() => {
    sessionStorage.setItem("postCheckout", "1");
    return () => sessionStorage.removeItem("postCheckout");
  }, []);

  // stable function (memoized) so the effect can depend on it without warnings
  const updateCompanyLimit = useCallback(
    async (sub) => {
      if (!user?.companyId) return;

      const teamSizeMap = {
        "4-10": 10,
        "11-20": 20,
        "21-35": 35,
        "36-50": 50,
      };

      const employeeLimit = teamSizeMap[sub.teamSize];

      if (!employeeLimit) {
        throw new Error(`Unknown paid team size: ${sub.teamSize}`);
      }

      const companyRef = doc(db, "companies", user.companyId);
      await updateDoc(companyRef, {
        plan: sub.planName,
        teamSize: sub.teamSize,
        employeeLimit,
        subscriptionStatus: sub.status,

        "subscription.planName": sub.planName,
        "subscription.teamSize": sub.teamSize,
        "subscription.employeeLimit": employeeLimit,
        "subscription.status": sub.status,
        "subscription.updatedAt": serverTimestamp(),
      });
    },
    [user?.companyId],
  );
  // 🔔 NEW: send welcome email once per company after a successful subscription
  const maybeSendWelcomeEmail = useCallback(async () => {
    if (!user?.companyId) return;
    const companyRef = doc(db, "companies", user.companyId);
    const snap = await getDoc(companyRef);
    const data = snap.data() || {};
    if (data.welcomeEmailSent) return; // already sent

    // Use company name if present; fall back to nothing
    const companyName = data.name || "";
    const toEmail = user.email;
    const toName =
      user.name ||
      user.displayName ||
      (toEmail ? toEmail.split("@")[0] : "there");

    await sendEmployerWelcomeEmail({ toEmail, toName, companyName });
    await updateDoc(companyRef, {
      welcomeEmailSent: true,
      welcomeEmailSentAt: serverTimestamp(),
    });
  }, [user?.companyId, user?.email, user?.name, user?.displayName]);

  useEffect(() => {
    if (loading || !user) return;

    const isActive =
      !!subscription && ["active", "trialing"].includes(subscription.status);

    if (!subscription) {
      setPhase("waiting");
      return;
    }

    if (isActive && !wroteOnceRef.current) {
      wroteOnceRef.current = true;
      (async () => {
        try {
          setPhase("linking");
          trackEvent("subscription_success_view", {
            status: subscription.status,
            plan_name: subscription.planName || "unknown",
            team_size: subscription.teamSize || "unknown",
          });
          await updateCompanyLimit(subscription);
          await maybeSendWelcomeEmail(); // ⟵ send once
          trackEvent("subscription_completed", {
            status: subscription.status,
            plan_name: subscription.planName || "unknown",
            team_size: subscription.teamSize || "unknown",
          });
          if (user?.uid) {
            await updateDoc(doc(db, "users", user.uid), {
              onboarding: {
                step: "dashboard",
                emailVerified: true,
                companyCreated: true,
                subscriptionStatus: subscription?.status || "active",
                lastUpdatedAt: serverTimestamp(),
              },
            });
          }

          setPhase("done");
          navigate("/admin-dashboard", { replace: true });
        } catch (err) {
          console.error("🔥 Error linking company plan:", err);
          trackEvent("subscription_success_error", {
            error_code: err.code || err.message || "unknown",
          });

          setPhase("error");
        }
      })();
    }
    // if sub exists but isn't active/trialing yet, we keep showing "waiting"
  }, [
    subscription,
    loading,
    user,
    navigate,
    updateCompanyLimit,
    setPhase,
    maybeSendWelcomeEmail,
  ]);

  return (
    <div style={{ maxWidth: 760, margin: "6rem auto", textAlign: "center" }}>
      <h2>Finishing your setup…</h2>
      {phase === "waiting" && (
        <p>
          We’re verifying your subscription with Stripe. This usually takes a
          moment.
        </p>
      )}
      {phase === "linking" && (
        <p>Linking your plan to your company and applying limits…</p>
      )}
      {phase === "done" && <p>All set. Redirecting to your dashboard…</p>}
      {phase === "error" && (
        <>
          <p style={{ color: "#b00" }}>
            Something went wrong linking your plan. You can continue.
          </p>
          <div style={{ marginTop: 16 }}>
            <button onClick={() => navigate("/admin-dashboard")}>
              Go to dashboard
            </button>{" "}
            <button onClick={() => navigate("/subscription")}>
              Manage subscription
            </button>
          </div>
        </>
      )}
    </div>
  );
}
